import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export type CatalogReviewDialogAction = "return" | "reject";

interface CatalogReviewActionDialogProps {
  open: boolean;
  action: CatalogReviewDialogAction | null;
  title?: string;
  loading?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (value: string) => Promise<void>;
}

export function CatalogReviewActionDialog({
  open,
  action,
  title,
  loading = false,
  onOpenChange,
  onConfirm,
}: CatalogReviewActionDialogProps) {
  const [value, setValue] = useState("");
  const [pending, setPending] = useState(false);
  const isReject = action === "reject";
  const busy = loading || pending;

  const handleOpenChange = (next: boolean) => {
    if (!next) setValue("");
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    if (!value.trim()) return;
    setPending(true);
    try {
      await onConfirm(value.trim());
      setValue("");
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {title ?? (isReject ? "Reject catalog record" : "Return for correction")}
          </DialogTitle>
          <DialogDescription>
            {isReject
              ? "Provide a reason for rejection. The creator will be notified and the record becomes view-only."
              : "Provide notes explaining what needs to be corrected before resubmission."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="review-action-notes">{isReject ? "Reason" : "Notes"}</Label>
          <Textarea
            id="review-action-notes"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={isReject ? "Reason for rejection..." : "Correction notes..."}
            rows={4}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={isReject ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={busy || !value.trim()}
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {isReject ? "Reject" : "Return"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
